import jsPDF from "jspdf";
import { drawProHeader, drawProFooter, getSettingsCached } from "./pdfHeader";

const TITLES = {
  baja: "CERTIFICADO DE BAJA MÉDICA",
  alta: "CERTIFICADO DE ALTA MÉDICA",
  justificante: "JUSTIFICANTE MÉDICO DE ASISTENCIA",
  ordinario: "CERTIFICADO MÉDICO ORDINARIO",
  aptitud: "CERTIFICADO MÉDICO DE APTITUD",
  informe: "INFORME MÉDICO",
};

const SUBTITLES = {
  baja: "Incapacidad Temporal · Uso privado",
  alta: "Reincorporación · Uso privado",
  justificante: "Asistencia a consulta médica",
  ordinario: "Certificado médico privado",
  aptitud: "Aptitud médica para actividad",
  informe: "Informe clínico privado",
};

function fmtDate(d) {
  if (!d) return "-";
  try { return new Date(d).toLocaleDateString("es-ES"); } catch { return d; }
}

function fmtDateTime(d) {
  if (!d) return "-";
  try { return new Date(d).toLocaleString("es-ES", { timeZone: "Europe/Madrid" }); } catch { return d; }
}

export async function generateCertificatePDF(cert) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const margin = 48;

  const settings = cert._settings || (await getSettingsCached());
  const doctor = cert._doctor || {
    name: cert.doctor_name,
    specialty: cert.doctor_specialty,
    license_number: cert.doctor_license,
  };

  const type = cert.cert_type || "ordinario";
  let y = await drawProHeader(doc, {
    docTitle: TITLES[type] || "CERTIFICADO MÉDICO",
    docSubtitle: SUBTITLES[type] || "",
    settings, doctor,
  });
  y += 14;

  // Code / date row
  doc.setFontSize(10);
  doc.setTextColor(100, 116, 139);
  doc.text(`Código: ${cert.code}`, margin, y);
  doc.text(new Date(cert.created_at).toLocaleString("es-ES"), W - margin, y, { align: "right" });
  y += 14;
  doc.text(`Verificación: ${cert.verification_code || "-"}`, margin, y);
  y += 18;

  doc.setDrawColor(226, 232, 240);
  doc.line(margin, y, W - margin, y);
  y += 18;

  // Patient block
  doc.setFillColor(248, 250, 252);
  doc.roundedRect(margin, y - 4, W - 2 * margin, 56, 6, 6, "F");
  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  doc.text("PACIENTE", margin + 12, y + 10);
  doc.setFontSize(12);
  doc.setTextColor(15, 23, 42);
  doc.setFont("helvetica", "bold");
  doc.text(cert.patient_name || "-", margin + 12, y + 26);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(71, 85, 105);
  if (cert.patient_nif) doc.text(`DNI/NIE: ${cert.patient_nif}`, margin + 12, y + 42);
  y += 70;

  // Type-specific "Certifica que..." block
  const label = (t) => {
    doc.setFontSize(11);
    doc.setTextColor(15, 118, 110);
    doc.setFont("helvetica", "bold");
    doc.text(t, margin, y);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(15, 23, 42);
    y += 14;
  };

  const paragraph = (text, size = 11) => {
    doc.setFontSize(size);
    doc.setTextColor(15, 23, 42);
    const lines = doc.splitTextToSize(text, W - 2 * margin);
    doc.text(lines, margin, y);
    y += lines.length * (size + 3) + 6;
  };

  const kv = (k, v) => {
    if (!v && v !== 0) return;
    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139);
    doc.text(k, margin, y);
    doc.setTextColor(15, 23, 42);
    const lines = doc.splitTextToSize(String(v), W - 2 * margin - 140);
    doc.text(lines, margin + 140, y);
    y += Math.max(14, lines.length * 13) + 4;
  };

  label("EL FACULTATIVO ABAJO FIRMANTE CERTIFICA QUE:");

  if (type === "baja") {
    paragraph(
      `El/La paciente arriba identificado/a se encuentra en situación de INCAPACIDAD TEMPORAL para el desempeño ` +
      `de su actividad habitual, siendo médicamente necesaria su baja laboral.`
    );
    y += 4;
    kv("Fecha de inicio:", fmtDate(cert.start_date));
    kv("Duración estimada:", cert.estimated_days ? `${cert.estimated_days} días` : (cert.end_date ? `hasta ${fmtDate(cert.end_date)}` : "a determinar"));
    if (cert.diagnosis) kv("Diagnóstico:", cert.diagnosis);
    if (cert.restrictions) kv("Restricciones:", cert.restrictions);
  } else if (type === "alta") {
    paragraph(
      `El/La paciente arriba identificado/a se encuentra en condiciones médicas para reincorporarse a su ` +
      `actividad habitual con efectos de la fecha indicada.`
    );
    y += 4;
    kv("Fecha de alta:", fmtDate(cert.start_date || cert.end_date));
    if (cert.diagnosis) kv("Diagnóstico previo:", cert.diagnosis);
    if (cert.restrictions) kv("Recomendaciones / restricciones:", cert.restrictions);
  } else if (type === "justificante") {
    paragraph(
      `El/La paciente arriba identificado/a ha acudido a esta consulta médica por motivos asistenciales ` +
      `en la fecha y hora que se indican a continuación.`
    );
    y += 4;
    kv("Fecha y hora de visita:", fmtDateTime(cert.visit_datetime || cert.created_at));
    if (cert.diagnosis) kv("Motivo (si procede):", cert.diagnosis);
  } else if (type === "aptitud") {
    const verdictMap = {
      apto: "APTO",
      no_apto: "NO APTO",
      apto_con_restricciones: "APTO CON RESTRICCIONES",
    };
    paragraph(
      `Tras la valoración médica realizada, se emite el presente certificado con el siguiente veredicto ` +
      `de aptitud para la actividad indicada.`
    );
    y += 4;
    kv("Actividad:", cert.fit_for || "-");
    kv("Veredicto:", verdictMap[cert.fit_verdict] || "-");
    if (cert.restrictions) kv("Restricciones:", cert.restrictions);
    if (cert.diagnosis) kv("Observaciones clínicas:", cert.diagnosis);
  } else if (type === "informe") {
    if (cert.diagnosis) { label("DIAGNÓSTICO"); paragraph(cert.diagnosis); }
    label("CONTENIDO CLÍNICO");
  } else {
    // ordinario
    label("CONTENIDO");
  }

  // Free body text
  if (cert.body) {
    paragraph(cert.body);
  }

  // Signature footer with QR + electronic signature
  const verifyUrl = `${window.location.origin}/verify/${cert.code}?v=${cert.verification_code || ""}`;
  await drawProFooter(doc, {
    settings, doctor,
    qrUrl: verifyUrl,
    qrLabel: "Verificar certificado",
    sigLabel: "Firma y sello del profesional",
    signature: cert.signature,
  });

  doc.save(`certificado-${type}-${cert.code}.pdf`);
}
